import { useEffect, useRef, useState } from 'react'
import { useClickAway } from 'react-use'

import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { useLenisStore } from '@/contexts/lenis'
import { useLayoutStore } from '@/store/layout'
import { useLocaleStore } from '@/store/locale'
import { useRouterStore } from '@/store/router'
import { LANGUAGES } from '@/constants'

import { cn } from '@/lib/utils'

const LangSelector = () => {
  const [open, setOpen] = useState(false)
  const [currentLocale, currentTranslations] = useLocaleStore((state) => [
    state.currentLocale,
    state.currentTranslations
  ])
  const root = useRef<HTMLDivElement>(null!)

  useClickAway(root, () => open && setOpen(false))

  useEffect(() => {
    const unsubscribe = useLayoutStore.subscribe(
      (state) => [state.menuOpen, state.modalOpen],
      (menuOpen, modalOpen) => {
        if (open && (menuOpen || modalOpen)) {
          setOpen(false)
        }
      }
    )

    return () => {
      unsubscribe()
    }
  }, [open])

  useEffect(() => {
    const unsubscribe = useLenisStore.subscribe(
      (state) => state.scroll,
      (scroll, prevScroll) => {
        if (open && scroll !== prevScroll) {
          setOpen(false)
        }
      }
    )

    return () => {
      unsubscribe()
    }
  }, [open])

  useEffect(() => {
    const unsubscribe = useRouterStore.subscribe(
      (state) => state.currentRoute,
      () => setOpen(false)
    )

    return () => {
      unsubscribe()
    }
  }, [])

  return (
    <div ref={root} className="static">
      <button
        className={cn(
          '-m-4 flex p-4',
          'transition-colors duration-500 ease-in-out-cubic'
        )}
        onClick={() => setOpen((open) => !open)}
      >
        <span className="inline-flex items-center space-x-2">
          <Typography variant="label" className="uppercase">
            {currentLocale}
          </Typography>
          <span
            className={cn(
              'flex h-[.8rem] w-[.8rem] items-center justify-center',
              'origin-center transition-transform duration-300 ease-in-out-cubic',
              { 'translate-y-[0.3rem] rotate-180': open }
            )}
          >
            <SVGIcon symbol="select-icon" className="h-[1rem] w-[1.1rem]" />
          </span>
        </span>
      </button>

      <div
        className={cn(
          'absolute -left-[1rem] top-[1.8rem] grid h-auto w-full',
          'transition-all duration-300 ease-in-out-cubic',
          { 'grid-rows-[0fr] opacity-0': !open },
          { 'grid-rows-[1fr] opacity-100': open }
        )}
      >
        <div className="w-[4rem] overflow-hidden">
          <ul
            className={cn(
              'flex flex-col space-y-[0.5rem] p-2',
              'border border-havana bg-light'
            )}
          >
            {LANGUAGES.map((lang: string) => (
              <li key={lang} className="flex w-full">
                <a
                  href={
                    currentTranslations?.find((t) => t.locale.includes(lang))
                      ?.href || `/${lang}`
                  }
                  className={cn('inline-flex grow p-2 text-dark', {
                    'pointer-events-none': lang === currentLocale
                  })}
                >
                  <Typography
                    variant="label"
                    className={cn('uppercase', {
                      'opacity-40': lang === currentLocale
                    })}
                  >
                    {lang}
                  </Typography>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

export default LangSelector
